import { req, setText } from './hudWrite';
import { clockRateText } from './world/clock';
import { devFlag, urlFlag, TEST_SWITCHES } from './urlFlags';

/** `?pilotwarprow=1`: the old WARP row back, shown only off 1x, which moved the panels stacked under it. `autowarp-check.mjs --leg stack --inject warp-row`. */
const pilotWarpRow = devFlag(() => TEST_SWITCHES && urlFlag('pilotwarprow'));

/** Where the direction of travel sits off the nose, in the ship's own frame. */
export type PilotTurnSide = 'LEFT' | 'RIGHT' | 'UP' | 'DOWN';

/**
 * The pilot's own instruments: cruise-only, relative to the dominant-pull
 * body (round 13's primary-or-nearest, whichever pulls hardest here). SPEED,
 * HEIGHT and NOSE OFF COURSE are always about that body and the ship's own
 * nose, and never move for a picked target - SPEED VS TARGET, DISTANCE,
 * CLOSEST, COURSE OFF TARGET (the direction of travel's own angle off the
 * line to it) and NOSE OFF TARGET are guidance rows about the target instead,
 * appearing alongside them rather than replacing them. Picking a target adds
 * rows; it never hides or relabels one because a target was picked, including
 * the HDG row, which used to swap NOSE OFF COURSE away for COURSE OFF TARGET
 * and take the off-prograde figure a pilot braking onto a target still needs
 * with it. CLOSEST is the one row that still relabels, but on a different
 * axis: once guided, whether the relative state is a bound orbit or an open
 * approach is its own fact, and the row reads LOW POINT with a recurring pass
 * in the first case and CLOSEST with a one-time flyby in the second - see
 * `closestClosed` below. TIME TO BURN is the one row that spans both: the
 * burn is always against the dominant pull, and its LATE cue is the only
 * place the two bodies are compared, which is why the cue is silent unless
 * they are the same body. Every rendered label and its styled hover tooltip
 * are plain words a non-pilot reads at a glance - see `PILOT_ROW_GLOSSARY`
 * below, the one source both the tooltips and `docs/VERIFICATION.md`'s
 * glossary table are written from.
 */
export interface PilotReadout {
  /** The dominant body's own display name, upper-cased. */
  bodyLabel: string;
  /** SPEED: the ship's own speed, metres per second. Always shown. */
  speedMps: number;
  /** HEIGHT: distance to the dominant body's centre minus its registry radius, clamped to 0. Always shown. */
  altitudeM: number;
  /** NOSE OFF COURSE: nose angle off the direction of travel, 0 prograde to 180 retrograde. Always shown, whether or not a target is picked. Null when the ship carries no speed to have a direction of travel. */
  headingOffDeg: number | null;
  /** NOSE OFF COURSE's own side: where the prograde marker sits off the nose, which is the way the nose has to swing to close the angle. Null dead ahead and dead astern, where no side is nearer than another. */
  headingOffSide: PilotTurnSide | null;
  /** SPEED TO MATCH: the whole speed relative to the picked target, which is what a rendezvous has to null. Guidance-only: null (and the row hidden) with nothing picked. */
  targetSpeedMps: number | null;
  /** SPEED VS TARGET: the rate the range to the target shuts, negative while it opens. Guidance-only: null (and the row hidden) with nothing picked. */
  closingMps: number | null;
  /** DISTANCE: distance to the target's surface. Guidance-only: null (and the row hidden) with nothing picked. */
  rangeM: number | null;
  /** COURSE OFF TARGET: angle between the direction of travel and the line to the target. Guidance-only: null (and the row hidden) with nothing picked, or when relative speed sits under the floor. */
  offTargetDeg: number | null;
  /** NOSE OFF TARGET: angle between the NOSE and the line to the target. Only set with a target picked; null (and the row hidden) otherwise. Carries no speed floor - the nose is defined whatever the ship is doing. */
  aimErrDeg: number | null;
  /** TIME TO BURN: seconds of full thrust to take one of the two speeds above to zero - `targetSpeedMps` with a target picked, `speedMps` without. Null with no thrust to spend, and null on an orbit the picked target already holds, the one BRAKE IN reads blank for, since no burn is owed to reach it. */
  burnSecs: number | null;
  /** TIME TO BURN's own cue: the burn no longer fits inside the time left to closest approach. */
  burnLate: boolean;
  /** CLOSEST (LOW POINT in a closed orbit): how near the ship's path comes to the target's surface, metres. Guidance-only: null (and the row hidden) with nothing picked, and null again when the state carries no closest approach to read. */
  closestM: number | null;
  /** Seconds to that closest approach, negative once it is behind the ship. Null alongside `closestM`. */
  closestSecs: number | null;
  /** True for a bound orbit, where the row reads LOW POINT and a recurring pass; false for an open approach, where it reads CLOSEST and a one-time flyby. Meaningless while `closestM` is null. */
  closestClosed: boolean;
  /** BRAKE IN: seconds until retrograde thrust has to start for the closest approach above to be reached at the speed of a circular orbit there. Zero and below reads NOW. Null when nothing is owed, when the approach is opening, when the path's own closest point stays outside the radius the target holds a ship in, or when that target's primary has time to rewrite the conic before the moment counted down to. */
  brakeSecs: number | null;
  /** BRAKE IN's own cue: the countdown is inside the band its own error leaves. */
  brakeAmber: boolean;
  /** True when `brakeSecs` is the two-body conic's own leave time rather than the drawn path's flown one - a third body can pull it off by a wide margin, which is why the row marks it rather than staying silent about it. */
  brakeApprox: boolean;
  /** Session delta-v spent, metres per second. */
  deltaVMps: number;
  /** Simulated seconds a real second the world clock has really moved over the
   *  last quarter second, WARP TO NEXT's jumps included. 1 is real time. */
  warpFactor: number;
  /** GO's Auto-warp is setting that pace. */
  warpAuto: boolean;
  /** The picked target's own display name, upper-cased, or null with nothing picked. */
  targetLabel: string | null;
  /** RESTING ON: the body the ship is sitting on, upper-cased. Null (and the row hidden) while it is flying. */
  groundedLabel: string | null;
  /** The key that launches a resting ship back into orbit, named in that row. */
  launchKey: string;
  /** The assist's own cap bound on the last step: it is spending and still falling behind the nose, which is the state the panel has to say out loud rather than burn quietly through. */
  assistCapped: boolean;
}

/** The predicted trajectory line: off, the coast path, or the held hand's. */
export type TrajectoryStatus = 'off' | 'coast' | 'held';

export interface PilotPanelFrame {
  /** The pilot's instruments, or null while a fight has the flight state frozen. */
  pilot: PilotReadout | null;
  /** What the predicted trajectory line currently is, for the toggle to name. */
  trajectory: TrajectoryStatus;
}

export interface PilotPanelDeps {
  /** Fired when the player clicks the pilot panel's Solar System link. */
  onSolarSystem?: () => void;
  /** Fired when the player clicks the pilot panel's trajectory toggle. */
  onTrajectoryToggle?: () => void;
}

export interface PilotPanel {
  update: (frame: PilotPanelFrame) => void;
}

/** One key, drawn as a cap the way the key list draws one. */
function keyCap(key: string): HTMLElement {
  const el = document.createElement('kbd');
  el.className = 'hud-key';
  el.textContent = key;
  return el;
}

/** m/s below 1000, km/s above - the pilot readouts' own units, not a game unit anywhere else. */
export function formatMpsForHud(mps: number): string {
  return Math.abs(mps) < 1000 ? `${Math.round(mps)} m/s` : `${(mps / 1000).toFixed(2)} km/s`;
}

/**
 * The smallest change `formatMpsForHud` can show at a speed. Exported because
 * `src/main.ts`'s SPD control offsets by it, and an offset under one step
 * would leave the painted text unchanged.
 */
export function speedDisplayStepMps(mps: number): number {
  return Math.abs(mps) < 1000 ? 1 : 10;
}

const AU_M = 1.495978707e11;

/**
 * A distance at any scale the pilot panel reaches, from metres to AU: whole
 * metres under 10 km, whole km under 100,000 km, two decimals of million km
 * under 100,000,000 km, two decimals of AU beyond that. One formatter for
 * HEIGHT, DISTANCE and CLOSEST (#510) and the target marker's own label in
 * `src/main.ts`, so none of them can ever disagree about a range - a warp
 * drop used to leave HEIGHT reading nine digits of km beside CLOSEST already
 * reading "M km".
 *
 * Exported because `src/main.ts` writes the target marker's own label with
 * it. `tools/preview/dojo/core.mjs`'s `parseAltText` parses this same shape
 * back out of the DOM; its tolerances are sized off this formatter's own
 * rounding, not the reverse.
 */
export function formatDistanceForHud(m: number): string {
  if (!Number.isFinite(m)) return '\u2014';
  if (m < 10_000) return `${Math.round(m)} m`;
  if (m < 1e8) return `${Math.round(m / 1000)} km`;
  if (m < 1e11) return `${(m / 1e9).toFixed(2)} M km`;
  return `${(m / AU_M).toFixed(2)} AU`;
}

/**
 * One line each, in plain words, on what a pilot-panel row means and, where
 * useful, what to do about it - the single source both the row's own styled
 * hover tooltip (#541: a `data-tooltip` read by the shared `src/hudTip.ts`,
 * no visible glyph beside the label) and `docs/VERIFICATION.md`'s glossary
 * table are written from.
 */
export const PILOT_ROW_GLOSSARY: ReadonlyArray<{ rowId: string; label: string; tip: string }> = [
  { rowId: 'pilot-target-row', label: 'TARGET',
    tip: 'Pick a body here to see your distance, speed vs it, and aim to it on the rows below. NONE turns those rows off.' },
  { rowId: 'pilot-ground-row', label: 'RESTING ON',
    tip: 'The body your ship is sitting on. Thrust alone will not lift you off most of them; use the key on the row below.' },
  { rowId: 'pilot-launch-row', label: 'LAUNCH',
    tip: 'The key that lifts a resting ship into a circular orbit.' },
  { rowId: 'pilot-speed-row', label: 'SPEED',
    tip: "Your speed relative to the body pulling on you most." },
  { rowId: 'pilot-targetspeed-row', label: 'SPEED TO MATCH',
    tip: 'Your speed relative to the target, in every direction. Burn it to zero to fly alongside.' },
  { rowId: 'pilot-burn-row', label: 'TIME TO BURN',
    tip: 'Full-thrust time to cancel SPEED TO MATCH, or SPEED with no target. LATE means it no longer fits before your closest pass.' },
  { rowId: 'pilot-closing-row', label: 'SPEED VS TARGET',
    tip: 'How fast the distance to your target is shrinking. Negative means it is growing.' },
  { rowId: 'pilot-alt-row', label: 'HEIGHT',
    tip: 'Your height above the body pulling on you most.' },
  { rowId: 'pilot-range-row', label: 'DISTANCE',
    tip: 'Straight-line distance to the target\'s surface.' },
  { rowId: 'pilot-closest-row', label: 'CLOSEST',
    tip: 'How near your path comes to the target, and when. OPENING means that point is already behind you.' },
  { rowId: 'pilot-brake-row', label: 'BRAKE IN',
    tip: 'Time until you must flip and brake to orbit. NOW means flip now. Blank when no brake is owed.' },
  { rowId: 'pilot-hdg-row', label: 'NOSE OFF COURSE',
    tip: 'Degrees between your nose and the way you are moving. The word beside it is the side to swing the nose to bring it down.' },
  { rowId: 'pilot-offtarget-row', label: 'COURSE OFF TARGET',
    tip: 'Degrees between the way you are moving and a straight line to your target. NOSE OFF TARGET reads where the nose points.' },
  { rowId: 'pilot-aim-row', label: 'NOSE OFF TARGET',
    tip: 'Degrees between your nose and a straight line to the target. Zero means you are aimed at it.' },
  { rowId: 'pilot-dv-row', label: 'DELTA-V SPENT',
    tip: 'Total speed change you have spent thrusting this session.' },
  { rowId: 'pilot-warp', label: 'WARP',
    tip: 'Game time per real second, including WARP TO NEXT jumps. AUTO means AUTO-WARP sets the pace.' },
  { rowId: 'pilot-traj-row', label: 'TRAJECTORY',
    tip: 'Draws your predicted path on the map. HELD keeps your throttle and stick as they are; COAST lets go of both.' },
];

/**
 * CLOSEST's one exception to every row above painting its label and tooltip
 * once at boot: a picked target can be a closed orbit or an open approach,
 * and those are different claims about the same number - a recurring low
 * point versus a one-time flyby - not a target picked or not, which is the
 * swap #511 removed. `pilot.closestClosed` picks this pair over
 * `PILOT_ROW_GLOSSARY`'s own `pilot-closest-row` entry each frame it changes.
 */
const PILOT_CLOSEST_CLOSED_LABEL = 'LOW POINT';
const PILOT_CLOSEST_CLOSED_TIP = 'The lowest point of your orbit, and when you pass it again.';

/** Seconds at any scale a burn or an approach reaches, from seconds to days. */
function formatDurationForHud(s: number): string {
  if (!Number.isFinite(s)) return '\u2014';
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 172_800) return `${(s / 3600).toFixed(1)} h`;
  return `${(s / 86_400).toFixed(1)} d`;
}

export function createPilotPanel(deps: PilotPanelDeps = {}): PilotPanel {
  const elPilotPanel = req<HTMLElement>('pilot-panel');
  const elPilotTitle = req<HTMLElement>('pilot-title');
  const elPilotGroundRow = req<HTMLElement>('pilot-ground-row');
  const elPilotGround = req<HTMLElement>('pilot-ground');
  const elPilotLaunchRow = req<HTMLElement>('pilot-launch-row');
  const elPilotLaunch = req<HTMLElement>('pilot-launch');
  const elPilotSpeed = req<HTMLElement>('pilot-speed');
  const elPilotAlt = req<HTMLElement>('pilot-alt');
  const elPilotHdg = req<HTMLElement>('pilot-hdg');
  const elPilotTargetSpeedRow = req<HTMLElement>('pilot-targetspeed-row');
  const elPilotTargetSpeed = req<HTMLElement>('pilot-targetspeed');
  const elPilotClosingRow = req<HTMLElement>('pilot-closing-row');
  const elPilotClosing = req<HTMLElement>('pilot-closing');
  const elPilotRangeRow = req<HTMLElement>('pilot-range-row');
  const elPilotRange = req<HTMLElement>('pilot-range');
  const elPilotOffTargetRow = req<HTMLElement>('pilot-offtarget-row');
  const elPilotOffTarget = req<HTMLElement>('pilot-offtarget');
  const elPilotAimRow = req<HTMLElement>('pilot-aim-row');
  const elPilotAim = req<HTMLElement>('pilot-aim');
  const elPilotBurn = req<HTMLElement>('pilot-burn');
  const elPilotClosestRow = req<HTMLElement>('pilot-closest-row');
  const elPilotClosestLabel = req<HTMLElement>('pilot-closest-label');
  const elPilotClosest = req<HTMLElement>('pilot-closest');
  const elPilotBrakeRow = req<HTMLElement>('pilot-brake-row');
  const elPilotBrake = req<HTMLElement>('pilot-brake');
  let lastBurnText = '';
  let lastClosestText = '';
  let lastClosestClosed: boolean | null = null;
  let lastBrakeText = '';
  let lastBrakeApprox: boolean | null = null;
  const elPilotAssistNotice = req<HTMLElement>('pilot-assist-notice');
  const elPilotDv = req<HTMLElement>('pilot-dv');
  const elPilotWarp = req<HTMLElement>('pilot-warp');
  let elOldWarpRow: HTMLElement | null = null;
  let elOldWarp: HTMLElement | null = null;
  if (pilotWarpRow()) {
    elOldWarpRow = document.createElement('div');
    elOldWarpRow.className = 'row pilot-help-row hidden';
    elOldWarpRow.innerHTML = '<span class="label">WARP</span><span class="val">&mdash;</span>';
    elOldWarp = elOldWarpRow.lastElementChild as HTMLElement;
    req<HTMLElement>('pilot-traj-row').before(elOldWarpRow);
  }
  // Every row's label and tooltip are fixed for the panel's life, so they
  // are painted once here off the one glossary object, rather than
  // duplicating the copy into index.html's markup as well as this module.
  // The tooltip lands on the row itself as `data-tooltip`, read by the
  // shared `initHudTips()` in src/hudTip.ts off every `.hud-tip` row (#541:
  // no visible badge, and no native title= - hovering the row is the only
  // affordance).
  for (const row of PILOT_ROW_GLOSSARY) {
    req<HTMLElement>(row.rowId).dataset.tooltip = row.tip;
  }
  // CLOSEST's own open-approach label/tooltip, captured off the array above
  // rather than repeated as a literal, since the loop already painted it
  // once and `update()` below only needs it back for the closed-orbit swap.
  const pilotClosestOpenRow = PILOT_ROW_GLOSSARY.find((row) => row.rowId === 'pilot-closest-row');
  if (!pilotClosestOpenRow) throw new Error('pilot-closest-row missing from PILOT_ROW_GLOSSARY');
  const pilotClosestOpenLabel = pilotClosestOpenRow.label;
  const pilotClosestOpenTip = pilotClosestOpenRow.tip;
  // BRAKE's own approx-leave-time swap: the base tip painted once above, with one sentence appended while the reading is the two-body conic's rather than the drawn path's flown one.
  const pilotBrakeRow = PILOT_ROW_GLOSSARY.find((row) => row.rowId === 'pilot-brake-row');
  if (!pilotBrakeRow) throw new Error('pilot-brake-row missing from PILOT_ROW_GLOSSARY');
  const pilotBrakeTip = pilotBrakeRow.tip;
  const pilotBrakeApproxTip = `${pilotBrakeTip} Target's pull only; the path line shows the flown figure.`;
  const elPilotSolarBtn = req<HTMLElement>('pilot-solar-btn');
  elPilotSolarBtn.addEventListener('click', () => { deps.onSolarSystem?.(); });
  const elPilotTrajBtn = req<HTMLElement>('pilot-traj-btn');
  elPilotTrajBtn.addEventListener('click', () => { deps.onTrajectoryToggle?.(); });
  let trajPaintKey = '';

  function update(frame: PilotPanelFrame): void {
    // ---------- The predicted trajectory's own switch ----------
    // HELD says the line assumes the hand stays where it is; COAST says it is
    // the path with nothing held, which is also what a warped clock leaves
    // (`HOLD_REAL_S` of real seconds is no sim seconds at all at a day a
    // second). Without the word the two lines look the same and mean
    // different things.
    if (frame.trajectory !== trajPaintKey) {
      trajPaintKey = frame.trajectory;
      elPilotTrajBtn.textContent = frame.trajectory.toUpperCase();
      elPilotTrajBtn.classList.toggle('active', frame.trajectory !== 'off');
    }

    // ---------- Pilot's own instruments (cruise only) ----------
    const pilot = frame.pilot;
    elPilotPanel.classList.toggle('hidden', pilot === null);
    if (pilot) {
      setText(elPilotTitle, `PILOT · ${pilot.bodyLabel}`);
      // SPEED, HEIGHT and NOSE OFF COURSE are the ship's own speed, its
      // altitude over the dominant pull (over the body under the hull while
      // resting), and its nose off its own direction of travel, always - a
      // picked target adds SPEED VS TARGET, DISTANCE,
      // COURSE OFF TARGET and NOSE OFF TARGET rather than taking any of the
      // three away. The HDG row used to swap NOSE OFF COURSE away for COURSE
      // OFF TARGET, dropping the one figure a pilot braking onto a target
      // still needs.
      const guided = pilot.targetLabel !== null;
      // The two rows that are up only while the ship is NOT flying: the body
      // under the hull, and the key off it. Two rows rather than one sentence
      // because the panel's value column is 122 px wide and one sentence
      // carrying both was cut off at "O TO LAU...". The key is drawn as a key
      // cap so a letter in a readout is not read as a digit.
      const grounded = pilot.groundedLabel !== null;
      elPilotGroundRow.classList.toggle('hidden', !grounded);
      elPilotLaunchRow.classList.toggle('hidden', !grounded);
      if (grounded) {
        setText(elPilotGround, pilot.groundedLabel ?? '');
        elPilotLaunch.replaceChildren(keyCap(pilot.launchKey),
          document.createTextNode(' TO ORBIT'));
      }
      setText(elPilotSpeed, formatMpsForHud(pilot.speedMps));
      setText(elPilotAlt, formatDistanceForHud(pilot.altitudeM));
      // One decimal and a side: the whole-degree reading took four passes to
      // settle on 180, and the angle alone never said which way round to go.
      setText(elPilotHdg, pilot.headingOffDeg === null ? '\u2014'
        : `${pilot.headingOffDeg.toFixed(1)}\u00b0${pilot.headingOffSide === null ? '' : ` ${pilot.headingOffSide}`}`);
      // SPEED TO MATCH, SPEED VS TARGET, DISTANCE, COURSE OFF TARGET and NOSE
      // OFF TARGET only mean anything with a target picked - each row carries
      // its own visibility, not an em-dash floor.
      elPilotTargetSpeedRow.classList.toggle('hidden', !guided);
      if (guided) setText(elPilotTargetSpeed, formatMpsForHud(pilot.targetSpeedMps ?? 0));
      elPilotClosingRow.classList.toggle('hidden', !guided);
      if (guided) setText(elPilotClosing, formatMpsForHud(pilot.closingMps ?? 0));
      elPilotRangeRow.classList.toggle('hidden', !guided);
      if (guided) setText(elPilotRange, formatDistanceForHud(pilot.rangeM ?? 0));
      elPilotOffTargetRow.classList.toggle('hidden', !guided);
      if (guided) {
        setText(elPilotOffTarget, pilot.offTargetDeg === null
          ? '—' : `${Math.round(pilot.offTargetDeg)}°`);
      }
      elPilotAimRow.classList.toggle('hidden', !guided);
      if (guided) {
        setText(elPilotAim, pilot.aimErrDeg === null
          ? '—' : `${Math.round(pilot.aimErrDeg)}°`);
      }
      // BURN and CLOSEST are the only rows that build their value out of two
      // parts, so they are the only two worth comparing before writing: the
      // string is cheap next to the layout the write dirties.
      const burnText = pilot.burnSecs === null ? '\u2014'
        : `${formatDurationForHud(pilot.burnSecs)}${pilot.burnLate ? ' LATE' : ''}`;
      if (burnText !== lastBurnText) {
        lastBurnText = burnText;
        elPilotBurn.textContent = burnText;
      }
      elPilotBurn.classList.toggle('pilot-late', pilot.burnLate);
      elPilotClosestRow.classList.toggle('hidden', !guided);
      if (guided) {
        if (pilot.closestClosed !== lastClosestClosed) {
          lastClosestClosed = pilot.closestClosed;
          elPilotClosestLabel.textContent = pilot.closestClosed ? PILOT_CLOSEST_CLOSED_LABEL : pilotClosestOpenLabel;
          elPilotClosestRow.dataset.tooltip = pilot.closestClosed ? PILOT_CLOSEST_CLOSED_TIP : pilotClosestOpenTip;
        }
        const closestText = pilot.closestM === null || pilot.closestSecs === null ? '\u2014'
          : pilot.closestSecs < 0 ? 'OPENING'
            : `${formatDistanceForHud(pilot.closestM)} in ${formatDurationForHud(pilot.closestSecs)}`;
        if (closestText !== lastClosestText) {
          lastClosestText = closestText;
          elPilotClosest.textContent = closestText;
        }
      }
      // BRAKE rides CLOSEST's own visibility - it is a time to the approach
      // that row is predicting - and reads an em dash inside it whenever the
      // countdown has no answer, which is also how it says the burn is done.
      elPilotBrakeRow.classList.toggle('hidden', !guided);
      if (guided) {
        const brakeText = pilot.brakeSecs === null ? '\u2014'
          : pilot.brakeSecs <= 0 ? 'NOW'
            : `${pilot.brakeApprox ? '~' : ''}${formatDurationForHud(pilot.brakeSecs)}`;
        if (brakeText !== lastBrakeText) {
          lastBrakeText = brakeText;
          elPilotBrake.textContent = brakeText;
        }
        elPilotBrake.classList.toggle('pilot-brake-soon', pilot.brakeAmber);
        if (pilot.brakeApprox !== lastBrakeApprox) {
          lastBrakeApprox = pilot.brakeApprox;
          elPilotBrakeRow.dataset.tooltip = pilot.brakeApprox ? pilotBrakeApproxTip : pilotBrakeTip;
        }
      }
      // The assist is spending and still losing to the gap. Painted off the
      // last step's own flag, so it clears itself the moment the cap stops
      // binding rather than latching.
      elPilotAssistNotice.classList.toggle('hidden', !pilot.assistCapped);
      setText(elPilotDv, formatMpsForHud(pilot.deltaVMps));
      // Always drawn, in a fixed-width cell beside the warp buttons: a readout
      // that came and went at 1x moved every panel stacked under the PILOT.
      const warpText = `${clockRateText(pilot.warpFactor)}${pilot.warpAuto ? ' AUTO' : ''}`;
      setText(elPilotWarp, warpText);
      if (elOldWarpRow !== null && elOldWarp !== null) {
        const warped = Math.abs(pilot.warpFactor - 1) > 0.02;
        elOldWarpRow.classList.toggle('hidden', !warped);
        if (warped) setText(elOldWarp, warpText);
      }
    }
  }

  return { update };
}
